import { useCallback, useEffect, useMemo, useState } from "react";
import { useDeliverables, usePhases } from "@/hooks/useLive";
import { useAuth } from "@/hooks/useAuth";
import { useChat } from "@/components/ChatContext";
import { useQueryClient } from "@tanstack/react-query";
import { FelixLauncher } from "@/components/FelixChat";
import { TimelineSpine } from "@/components/TimelineSpine";
import { ArrowUpRight, ExternalLink, Flag } from "lucide-react";
import { CATEGORIES, STATUS_MAP } from "@/data/categories";
import { cn, daysUntil, parseLaunchDate, primaryOwner } from "@/lib/utils";
import { setTaskFilter } from "@/lib/taskFilter";
import type { Deliverable, Status } from "@/lib/types";

interface Props {
  onJumpTo: (section: "timeline" | "risks" | "tasks" | "deliverables" | "criteria") => void;
}

function TaskQuickRow({ d, now, onJump }: { d: Deliverable; now: Date; onJump: () => void }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<Status>(d.status);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLocal(d.status); }, [d.status]);
  const parsed = parseLaunchDate(d.due);
  const days = parsed ? daysUntil(parsed, now) : null;
  const overdue = days !== null && days < 0 && local !== "complete";

  const handleStatus = useCallback(async (next: Status) => {
    const prev = local;
    setLocal(next);
    setSaving(true);
    try {
      await fetch(`/api/deliverables/${d.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
        credentials: "include",
      });
      qc.invalidateQueries({ queryKey: ["deliverables"] });
    } catch { setLocal(prev); }
    finally { setSaving(false); }
  }, [d.id, local, qc]);

  return (
    <div className="group flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/40">
      <button onClick={onJump} className="min-w-0 flex-1 text-left">
        <div className="flex items-center gap-2">
          {overdue && <Flag size={11} className="shrink-0 text-destructive" />}
          <span className="truncate text-caption font-medium text-foreground">{d.name}</span>
          {d.link && (
            <a
              href={d.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
            >
              <ExternalLink size={11} />
            </a>
          )}
        </div>
        <div className="mt-0.5 text-micro text-muted-foreground">
          {d.category && <span>{CATEGORIES[d.category]?.label}</span>}
          {d.phase && <span> · {d.phase}</span>}
        </div>
      </button>
      <span className={cn("shrink-0 text-micro tabular", overdue ? "font-semibold text-destructive" : "text-muted-foreground")}>
        {d.due || "—"}
      </span>
      <select
        value={local}
        onChange={(e) => handleStatus(e.target.value as Status)}
        onClick={(e) => e.stopPropagation()}
        disabled={saving}
        className={cn("shrink-0 rounded-md border border-border bg-card px-1.5 py-0.5 text-micro focus:outline-none focus:ring-1 focus:ring-primary", saving && "opacity-50 cursor-wait")}
      >
        {(Object.keys(STATUS_MAP) as Status[]).map((k) => (
          <option key={k} value={k}>{STATUS_MAP[k].label}</option>
        ))}
      </select>
    </div>
  );
}

export function OverviewPanel({ onJumpTo }: Props) {
  const { user } = useAuth();
  const { drawerOpen } = useChat();
  const DELIVERABLES = useDeliverables();
  const PHASES = usePhases();
  const now = useMemo(() => new Date(), []);

  const emailLocal = (user?.email ?? "").split("@")[0].toLowerCase();
  const firstName = user?.name?.split(" ")[0] ?? "there";
  const activePhase = PHASES.find((p) => p.status === "active");

  // "Your work in this current phase": tasks in active phase assigned to me
  const myPhaseTasks = useMemo(() => {
    if (!emailLocal) return [];
    const phaseName = activePhase?.name ?? "";
    return DELIVERABLES
      .filter((d) => d.status !== "complete")
      .filter((d) => d.owner.toLowerCase().includes(emailLocal))
      .filter((d) => !phaseName || d.phase === phaseName)
      .sort((a, b) => {
        const aP = parseLaunchDate(a.due)?.getTime() ?? Infinity;
        const bP = parseLaunchDate(b.due)?.getTime() ?? Infinity;
        return aP - bP;
      });
  }, [DELIVERABLES, emailLocal, activePhase]);

  // "This week" — tasks assigned to me with a parseable due in the next 7 days
  // OR overdue. Mirrors the Monday digest email so the dashboard and email
  // tell the same story.
  const myWeekBuckets = useMemo(() => {
    if (!emailLocal) return { overdue: [], today: [], next7: [] };
    const overdue: Deliverable[] = [];
    const today: Deliverable[] = [];
    const next7: Deliverable[] = [];
    for (const d of DELIVERABLES) {
      if (d.status === "complete") continue;
      if (!d.owner.toLowerCase().includes(emailLocal)) continue;
      const parsed = parseLaunchDate(d.due);
      if (!parsed) continue;
      const days = daysUntil(parsed, now);
      if (days < 0) overdue.push(d);
      else if (days === 0) today.push(d);
      else if (days <= 7) next7.push(d);
    }
    const byDue = (a: Deliverable, b: Deliverable) =>
      (parseLaunchDate(a.due)?.getTime() ?? 0) - (parseLaunchDate(b.due)?.getTime() ?? 0);
    overdue.sort(byDue); today.sort(byDue); next7.sort(byDue);
    return { overdue, today, next7 };
  }, [DELIVERABLES, emailLocal, now]);
  const myWeekCount = myWeekBuckets.overdue.length + myWeekBuckets.today.length + myWeekBuckets.next7.length;

  // Group active-phase tasks by category
  const myGroupsByCategory = useMemo(() => {
    const map = new Map<string, Deliverable[]>();
    myPhaseTasks.forEach((d) => {
      if (!map.has(d.category)) map.set(d.category, []);
      map.get(d.category)!.push(d);
    });
    return Array.from(map.entries());
  }, [myPhaseTasks]);

  const jumpToTasksWithMe = () => {
    setTaskFilter({ owner: primaryOwner(user?.name ?? emailLocal) });
    onJumpTo("tasks");
  };

  const jumpToTasksByCategory = (category: string) => {
    setTaskFilter({ owner: primaryOwner(user?.name ?? emailLocal), category });
    onJumpTo("tasks");
  };

  return (
    <div className="space-y-12">
      {!drawerOpen && <FelixLauncher />}

      {/* Your week — overdue + today + next 7 days */}
      {myWeekCount > 0 && (
        <section>
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="text-h3 font-semibold text-foreground">
                Your week, {firstName}
              </h2>
              <p className="mt-0.5 text-caption text-muted-foreground">
                What needs your attention in the next 7 days. Same content as the Monday digest email.
              </p>
            </div>
            <button
              onClick={jumpToTasksWithMe}
              className="inline-flex shrink-0 items-center gap-1 text-caption font-medium text-primary hover:underline"
            >
              All my tasks <ArrowUpRight size={13} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              { key: "overdue", label: "Overdue", items: myWeekBuckets.overdue, accent: "border-destructive/40 bg-destructive/5", chip: "bg-destructive/10 text-destructive" },
              { key: "today", label: "Today", items: myWeekBuckets.today, accent: "border-primary/40 bg-primary/5", chip: "bg-primary/10 text-primary" },
              { key: "next7", label: "Next 7 days", items: myWeekBuckets.next7, accent: "border-border bg-card", chip: "bg-muted text-muted-foreground" },
            ].map((bucket) => (
              <div key={bucket.key} className={cn("rounded-xl border p-4", bucket.accent)}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-caption font-semibold text-foreground">{bucket.label}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-micro font-medium", bucket.chip)}>{bucket.items.length}</span>
                </div>
                {bucket.items.length === 0 ? (
                  <div className="text-micro text-muted-foreground">Nothing here. 🎉</div>
                ) : (
                  <ul className="space-y-1.5">
                    {bucket.items.slice(0, 5).map((d) => (
                      <li key={d.id} className="flex items-center justify-between gap-2">
                        <button onClick={jumpToTasksWithMe} className="min-w-0 flex-1 truncate text-left text-caption text-foreground hover:text-primary">
                          {d.name}
                        </button>
                        <span className="shrink-0 text-micro text-muted-foreground">{d.due}</span>
                      </li>
                    ))}
                    {bucket.items.length > 5 && (
                      <li className="text-micro text-muted-foreground">+ {bucket.items.length - 5} more</li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Your work in this current phase */}
      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-h3 font-semibold text-foreground">
              Your work in {activePhase?.name ? <span className="text-primary">{activePhase.name}</span> : "this phase"}
            </h2>
            <p className="mt-0.5 text-caption text-muted-foreground">
              Tasks assigned to you in the active phase — click any task to open the full list.
            </p>
          </div>
          <button
            onClick={jumpToTasksWithMe}
            className="inline-flex shrink-0 items-center gap-1 text-caption font-medium text-primary hover:underline"
          >
            All my tasks <ArrowUpRight size={13} />
          </button>
        </div>

        {myPhaseTasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card px-6 py-10 text-center">
            <p className="text-body font-medium text-foreground">Hey {firstName}, nothing assigned to you in {activePhase?.name ?? "this phase"}.</p>
            <p className="mt-1 text-caption text-muted-foreground">
              Enjoy the breather — or jump to the full task list below.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {myGroupsByCategory.map(([cat, items]) => {
              const catMeta = CATEGORIES[cat as keyof typeof CATEGORIES];
              return (
                <div
                  key={cat}
                  className="overflow-hidden rounded-xl border border-border bg-card"
                  style={catMeta ? { borderLeft: `4px solid ${catMeta.color}` } : undefined}
                >
                  <button
                    onClick={() => jumpToTasksByCategory(cat)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-body font-semibold text-foreground">
                        {catMeta?.label ?? cat}
                      </span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-micro tabular text-muted-foreground">
                        {items.length}
                      </span>
                    </div>
                    <span className="text-caption text-primary">Open <ArrowUpRight size={11} className="inline" /></span>
                  </button>
                  <div className="border-t border-border bg-muted/10">
                    {items.map((d) => (
                      <TaskQuickRow key={d.id} d={d} now={now} onJump={() => jumpToTasksByCategory(cat)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Horizontal phase showcase at bottom */}
      <section>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-h3 font-semibold text-foreground">Phases</h2>
            <p className="mt-0.5 text-caption text-muted-foreground">The whole launch timeline at a glance.</p>
          </div>
          <button
            onClick={() => onJumpTo("timeline")}
            className="inline-flex shrink-0 items-center gap-1 text-caption font-medium text-primary hover:underline"
          >
            Gates & dates <ArrowUpRight size={13} />
          </button>
        </div>
        <div className="-mx-4 overflow-x-auto scrollbar-thin px-4 md:mx-0 md:overflow-visible md:px-0">
          <div className="min-w-[760px] md:min-w-0">
            <TimelineSpine phases={PHASES} />
          </div>
        </div>
      </section>
    </div>
  );
}
